import type {StatusBadgeProps} from '../status-badge/status-badge';

// Status type for newsletter and broadcast editions
export type EditionStatus =
    | 'NEW'
    | 'SCHEDULED'
    | 'SENDING'
    | 'COMPARISON_SENDING'
    | 'COMPARISON_WAITING'
    | 'SENT'
    | 'TEMPLATE'
    | 'ARCHIVED'
    | 'HALTED'
    | 'IN_REVIEW'
    | 'PAUSED'
    | 'RETIRED'
    | 'RETIRING'
    | 'CANCELLED'
    | 'USER_TEMPLATE_PUBLISHED'
    | 'GENERATING'
    | 'GENERATED_WITH_ISSUES';

// Status type for drip editions
export type DripEditionStatus =
    | 'NEW'
    | 'COMPARISON_SENDING'
    | 'COMPARISON_WAITING'
    | 'HALTED'
    | 'IN_REVIEW'
    | 'SCHEDULED'
    | 'SENDING'
    | 'SENT'
    | 'TEMPLATE'
    | 'ARCHIVED'
    | 'PAUSED'
    | 'RETIRING'
    | 'RETIRED'
    | 'CANCELLED'
    | 'USER_TEMPLATE_PUBLISHED'
    | 'GENERATING'
    | 'GENERATED_WITH_ISSUES';

// Status type for drip sequences
export type DripSequenceStatus =
    | 'DRAFT'
    | 'ACTIVE'
    | 'PAUSED'
    | 'CANCELLED'
    | 'RETIRED'
    | 'ARCHIVED'
    | 'PROBLEM'
    | 'GENERATING'
    | 'GENERATED_WITH_ISSUES';

// Internal type for this component to make status badges for all editions
type Status =
    | 'NEW'
    | 'DRAFT'
    | 'SCHEDULED'
    | 'SENDING'
    | 'COMPARISON_SENDING'
    | 'COMPARISON_WAITING'
    | 'SENT'
    | 'TEMPLATE'
    | 'HALTED'
    | 'IN_REVIEW'
    | 'ARCHIVED'
    | 'ACTIVE'
    | 'PAUSED'
    | 'RETIRING'
    | 'RETIRED'
    | 'CANCELLED'
    | 'PROBLEM'
    | 'USER_TEMPLATE_PUBLISHED'
    | 'GENERATING'
    | 'GENERATED_WITH_ISSUES';

export function getEditionStatusInfo(status: Status): StatusBadgeProps | null | undefined {
    switch (status) {
        case 'SENT':
            return {level: 'success', text: 'Sent'};
        case 'SENDING':
            return {level: 'warning', text: 'Sending…'};
        case 'COMPARISON_SENDING':
            return {level: 'warning', text: 'Sending A/B test…'};
        case 'COMPARISON_WAITING':
            return {level: 'warning', text: 'Sending A/B test…'};
        case 'SCHEDULED':
            return {level: 'warning', text: 'Scheduled'};
        case 'NEW':
        case 'DRAFT':
            return {level: 'info', text: 'Draft'};
        case 'TEMPLATE':
            // We no longer display a status badge for starter template thumbnails
            return null;
        case 'USER_TEMPLATE_PUBLISHED':
            return {level: 'info', text: 'Template'};
        case 'ARCHIVED':
            return {level: 'hidden', text: 'Archived'};
        case 'HALTED':
            return {level: 'alert', text: 'Halted'};
        case 'IN_REVIEW':
            return {level: 'alert', text: 'Under review'};
        case 'ACTIVE':
            return {level: 'success', text: 'Active'};
        case 'PAUSED':
            return {level: 'warning', text: 'Paused'};
        case 'RETIRING':
            return {level: 'alert', text: 'Retiring…'};
        case 'RETIRED':
            return {level: 'alert', text: 'Retired'};
        case 'CANCELLED':
            return {level: 'alert', text: 'Canceled'};
        case 'GENERATING':
        case 'GENERATED_WITH_ISSUES':
        default:
            return null;
    }
}

export function getDripEditionStatusInfo(status: Status): StatusBadgeProps | null | undefined {
    switch (status) {
        case 'SENT':
            return {level: 'success', text: 'Sent'};
        case 'SENDING':
            return {level: 'success', text: 'Active'};
        case 'SCHEDULED':
            return {level: 'warning', text: 'Scheduled'};
        case 'NEW':
        case 'DRAFT':
            return {level: 'info', text: 'Draft'};
        case 'TEMPLATE':
            return {level: 'info', text: 'Template'};
        case 'ARCHIVED':
            return {level: 'hidden', text: 'Archived'};
        case 'HALTED':
            return {level: 'alert', text: 'Halted'};
        case 'IN_REVIEW':
            return {level: 'alert', text: 'Under review'};
        case 'ACTIVE':
            return {level: 'success', text: 'Active'};
        case 'PAUSED':
            return {level: 'warning', text: 'Paused'};
        case 'RETIRING':
            return {level: 'alert', text: 'Retiring…'};
        case 'RETIRED':
            return {level: 'alert', text: 'Retired'};
        case 'CANCELLED':
            return {level: 'alert', text: 'Canceled'};
        case 'PROBLEM':
            return {level: 'alert', text: 'Problem'};
        default:
            return null;
    }
}

export function getTemplateEditionStatusInfo(status: Status): StatusBadgeProps {
    switch (status) {
        case 'NEW':
            return {level: 'info', text: 'Unpublished template'};
        case 'USER_TEMPLATE_PUBLISHED':
            return {level: 'success', text: 'Published template'};
        case 'ARCHIVED':
            return {level: 'hidden', text: 'Archived'};
        default:
            return {level: 'info', text: 'Template'};
    }
}
